import React, { useEffect, useState } from 'react';
import { mfaService, MfaStatus } from '../../services/mfaService';

const BackupCodesDisplay: React.FC<{ codes: string[]; onDismiss: () => void }> = ({ codes, onDismiss }) => (
  <div className="p-4 bg-amber-50 border border-amber-200 rounded-md space-y-3">
    <p className="text-sm font-medium text-amber-900">Save these backup codes now</p>
    <p className="text-xs text-amber-800">
      Each code works once if you lose your authenticator. They will not be shown again.
    </p>
    <div className="grid grid-cols-2 gap-2">
      {codes.map((c) => (
        <code key={c} className="text-sm font-mono tracking-wide text-gray-900">
          {c}
        </code>
      ))}
    </div>
    <button
      type="button"
      onClick={onDismiss}
      className="px-3 py-1.5 text-sm font-medium text-amber-900 bg-amber-100 rounded-md hover:bg-amber-200"
    >
      I&apos;ve saved these codes
    </button>
  </div>
);

const MfaProfileSection: React.FC = () => {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [setupInfo, setSetupInfo] = useState<{ secret: string; provisioning_uri: string } | null>(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regenPassword, setRegenPassword] = useState('');
  const [regenCode, setRegenCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      setStatus(await mfaService.getStatus());
    } catch {
      setMessage({ type: 'error', text: 'Could not load MFA status.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleStartSetup = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const info = await mfaService.startSetup();
      setSetupInfo(info);
    } catch (err: any) {
      const detail = err.response?.data?.detail ?? err.response?.data?.message;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to start MFA setup.' });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const result = await mfaService.confirmSetup(confirmCode);
      setSetupInfo(null);
      setConfirmCode('');
      setNewBackupCodes(result.backup_codes);
      setMessage({ type: 'success', text: 'Two-factor authentication is now enabled.' });
      await refresh();
    } catch (err: any) {
      const detail = err.response?.data?.detail ?? err.response?.data?.message;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Invalid verification code.' });
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const result = await mfaService.regenerateBackupCodes(regenPassword, regenCode);
      setRegenPassword('');
      setRegenCode('');
      setShowRegenerate(false);
      setNewBackupCodes(result.backup_codes);
      setMessage({ type: 'success', text: 'New backup codes generated.' });
      await refresh();
    } catch (err: any) {
      const detail = err.response?.data?.detail ?? err.response?.data?.message;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Could not regenerate backup codes.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await mfaService.disable(disablePassword, disableCode);
      setDisablePassword('');
      setDisableCode('');
      setMessage({ type: 'success', text: 'Two-factor authentication disabled.' });
      await refresh();
    } catch (err: any) {
      const detail = err.response?.data?.detail ?? err.response?.data?.message;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Could not disable MFA.' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading security settings…</p>;
  }

  if (!status) {
    return (
      <div className="mt-8 pt-8 border-t border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Two-factor authentication</h3>
        <p className="text-sm text-red-700">
          {message?.text ?? 'Could not load MFA settings. Try refreshing the page.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 pt-8 border-t border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-2">Two-factor authentication</h3>
      <p className="text-sm text-gray-600 mb-4">
        Policy for your account: <span className="font-medium">{status.policy}</span>
        {status.required_by_policy && !status.enabled && (
          <span className="ml-2 text-amber-700">— setup required</span>
        )}
      </p>

      {message && (
        <div
          className={`mb-4 p-3 rounded-md text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {newBackupCodes && (
        <div className="mb-4">
          <BackupCodesDisplay codes={newBackupCodes} onDismiss={() => setNewBackupCodes(null)} />
        </div>
      )}

      {status.enabled ? (
        <div className="space-y-4">
          <p className="text-sm text-green-700">Authenticator app is enabled.</p>
          <p className="text-sm text-gray-600">
            Backup codes remaining: <span className="font-medium">{status.backup_codes_remaining}</span>
          </p>
          <p className="text-sm text-gray-500">
            You can also sign in with a one-time email code or a backup recovery code at login.
          </p>

          {!showRegenerate ? (
            <button
              type="button"
              onClick={() => setShowRegenerate(true)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Regenerate backup codes
            </button>
          ) : (
            <form onSubmit={handleRegenerate} className="space-y-3 max-w-md p-4 bg-gray-50 rounded-md border">
              <p className="text-sm text-gray-600">
                This replaces all existing backup codes. Confirm with your password and authenticator code.
              </p>
              <input
                type="password"
                placeholder="Current password"
                value={regenPassword}
                onChange={(e) => setRegenPassword(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                required
              />
              <input
                inputMode="numeric"
                maxLength={6}
                placeholder="Authenticator code"
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm tracking-widest"
                required
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Generate new codes
                </button>
                <button
                  type="button"
                  onClick={() => setShowRegenerate(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {status.policy === 'optional' && (
            <form onSubmit={handleDisable} className="space-y-3 max-w-md pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">Disable MFA with your password and a current authenticator code.</p>
              <input
                type="password"
                placeholder="Current password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                required
              />
              <input
                inputMode="numeric"
                maxLength={6}
                placeholder="Authenticator code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm tracking-widest"
                required
              />
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50"
              >
                Disable MFA
              </button>
            </form>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {!setupInfo ? (
            <button
              type="button"
              onClick={handleStartSetup}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {status.required_by_policy ? 'Set up MFA (required)' : 'Enable MFA'}
            </button>
          ) : (
            <form onSubmit={handleConfirmSetup} className="space-y-3 max-w-lg">
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <p className="text-xs text-gray-600 mb-2">Add this account in your authenticator app:</p>
                <p className="text-xs break-all font-mono text-gray-800 mb-3">{setupInfo.provisioning_uri}</p>
                <p className="text-xs text-gray-600">Manual key:</p>
                <p className="text-sm font-mono tracking-wider">{setupInfo.secret}</p>
              </div>
              <input
                inputMode="numeric"
                maxLength={6}
                placeholder="6-digit code"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm tracking-widest"
                required
              />
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Confirm and enable
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default MfaProfileSection;
